<template>
  <component
    :is="useAutocomplete ? VAutocomplete : VSelect"
    ref="selectComponentRef"
    v-bind="
      mergeProps(props, $attrs, attrs, {
        items: typedItems,
        variant: $attrs.variant || 'outlined',
        itemValue: itemValue || 'value',
        autocomplete: useAutocomplete ? 'nope' : '',
        'onUpdate:model-value': () => null,
        onBlur: () => null,
      })
    "
    v-model="internalValue"
    v-set-test-id="['SelectField', testId || getModelExpression()]"
    @blur="onBlur"
  >
    <template
      #item="{
        item,
        props: listItemProps,
      }: {
        item: SelectFieldItem;
        props: Record<string, unknown>;
      }"
    >
      <v-divider v-if="item.type === SelectFieldType.divider" />
      <v-list-subheader v-else-if="item.type === SelectFieldType.header">{{
        item.header
      }}</v-list-subheader>
      <v-list-item v-else v-bind="listItemProps" />
    </template>
    <template v-for="(_, name) in slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}" />
    </template>
    <template v-if="newItemLabel" #append-item>
      <v-list-item variant="text" @click="onClickNewItem">
        <template #prepend>
          <v-list-item-media class="mr-3">
            <font-awesome-icon :icon="faPlus" size="lg" />
          </v-list-item-media>
        </template>
        <v-list-item-title class="text-primary">
          {{ newItemLabel }}
        </v-list-item-title>
      </v-list-item>
    </template>
    <template #loader="{ isActive }">
      <base-linear-loader :is-visible="isActive" />
    </template>
  </component>
</template>

<script lang="ts" setup>
import type { Directive } from 'vue';
import { computed, ref, watch, mergeProps, useSlots } from 'vue';
import { VSelect, VAutocomplete } from 'vuetify/components';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { get } from 'underscore';
import type {
  FormFieldProps,
  FormFieldEmits,
} from '@/common/components/form/FormField.vue';
import {
  getAttributes,
  useValue,
} from '@/common/components/form/FormField.vue';
import type {
  ActionableComponentProps,
  TestIdInput,
} from '@/common/components/ActionableComponent';
import {
  attrName,
  getTestIdString,
  getModelExpression,
} from '@/common/components/ActionableComponent';
import type {
  SelectFieldDefaultItem,
  SelectFieldItem,
} from '@/common/components/form/SelectFieldTypes';
import { SelectFieldType } from '@/common/components/form/SelectFieldTypes';

const getActionableElement = (
  el: Element,
  selector: string,
): Element | undefined => {
  const inputEl = el.querySelector(selector);
  return inputEl || undefined;
};

const setTestId = (el: Element, id: string) => {
  el.setAttribute(attrName, id);
  getActionableElement(el, 'input')?.setAttribute(
    attrName,
    `${id}-query-input`,
  );
  getActionableElement(el, '.v-input__icon')?.setAttribute(
    attrName,
    `${id}-menu-open`,
  );
  getActionableElement(el, 'button.v-icon.fa-times-circle')?.setAttribute(
    attrName,
    `${id}-clear-button`,
  );
};

export type Props = ActionableComponentProps &
  Omit<FormFieldProps, 'items'> & {
    items?: (SelectFieldItem | SelectFieldDefaultItem)[];
    addSearchItemCount?: number | string;
    newItemLabel?: string;
    autoSelectOneOption?: boolean;
    itemValue?: string;
  };
const props = withDefaults(defineProps<Props>(), {
  addSearchItemCount: 20,
});

const slots = useSlots();

const vSetTestId: Directive = {
  mounted(el, { value }) {
    const id = getTestIdString(value as TestIdInput);
    setTestId(el, id);
  },
  updated(el, { value }) {
    const id = getTestIdString(value as TestIdInput);
    setTestId(el, id);
  },
};
type Emits = FormFieldEmits & { (e: 'click:new-item'): void };
const emit = defineEmits<Emits>();

const internalValue = useValue(props, emit);
const attrs = getAttributes(props);

const onBlur = () => {
  props.validation?.$touch();
  emit('blur');
};

const useAutocomplete = computed<boolean>(
  () =>
    Array.isArray(props.items) &&
    props.items.length >= parseFloat(`${props.addSearchItemCount}`),
);

const selectComponentRef = ref<
  typeof VSelect & { $refs: { menu?: { isActive?: boolean } } }
>();

const onClickNewItem = () => {
  emit('click:new-item');

  // this is a bug in VSelect where the menu is not closed when clicking on a list item added via slot #append-item
  if (selectComponentRef.value?.$refs.menu) {
    selectComponentRef.value.$refs.menu.isActive = false;
  }
};

watch(
  () => [props.items, props.autoSelectOneOption],
  () => {
    if (!props.autoSelectOneOption || props.items?.length !== 1) {
      return;
    }
    const v: unknown =
      typeof props.items[0] === 'object'
        ? get(
            props.items[0] as Record<string, unknown>,
            props.itemValue || 'value',
          )
        : props.items[0];
    emit('update:model-value', v);
  },
);

const typedItems = computed<SelectFieldItem[]>(() =>
  (props.items || []).map(item => ({
    type: SelectFieldType.default,
    ...item,
  })),
);
</script>
